<template>
    <div
        :class="['navbar__menu-item-wrapper', {'navbar__menu-item-wrapper--close-submenu': isSubmenuClosed}]"
        :data-testid="`navbar-menu-item-${index}`"
        @mouseenter="isSubmenuClosed = false"
    >
        <Component
            :is="menu.href ? NuxtLink : 'button'"
            v-bind="
                menu.href
                    ? {to: $withLocalePrefix(menu.href), exactActiveClass: 'navbar__menu-item--active', external: !isLocaleRoute(menu.href)}
                    : {}
            "
            :class="['navbar__menu-item', {'navbar__menu-item--active': isMenuItemActive}]"
            :aria-expanded="menu.items ? props.isOpen : undefined"
            :aria-controls="menu.items ? `navbar-submenu-${props.index}` : undefined"
            :external="!!menu.href && !isLocaleRoute(menu.href)"
            @click="onHandleClick"
        >
            <div class="navbar__menu-item-label-wrapper">
                <div name="navbar-menu-item-label" class="navbar__menu-item-label">{{ menu.label }}</div>
                <div name="navbar-menu-item-submenu-labels" class="navbar__menu-item-submenu-labels">
                    {{ submenuLabels }}
                </div>
            </div>
            <div class="navbar__menu-item-icon-wrapper">
                <VJoyIcon name="arrow-right" color="neutral-50" />
            </div>
        </Component>

        <NavbarSubmenu
            v-if="menu.items"
            :sections="menu.items"
            :feature-cards="menu.section"
            :is-open="isOpen"
            :parent-index="index"
            @close-menu="closeSubmenu"
        />
    </div>
</template>

<script setup lang="ts">
    import {computed, ref} from 'vue';
    import type {NavbarItem} from '@navbar-unlogged-types';
    import {NuxtLink} from '#components';
    import {useRoute} from '#imports';
    import {useRouteHelper} from '@malt/utils';
    import {VJoyIcon} from '@maltjoy/core-vue';
    import NavbarSubmenu from './NavbarSubmenu.vue';

    const props = defineProps<{
        menu: NavbarItem;
        index: number;
        isOpen: boolean;
    }>();

    const emits = defineEmits<{
        click: [];
        'close-menu': [];
    }>();

    const route = useRoute();
    const {isLocaleRoute} = useRouteHelper();
    const isMenuItemActive = computed(() => props.menu.items?.some((section) => section.items?.some((item) => route.path === item.href)) ?? false);

    const submenuLabels = computed(() => props.menu.items?.flatMap((section) => section.items?.map((item) => item.label) ?? []).join(', '));

    const isSubmenuClosed = ref(false);
    function closeSubmenu() {
        isSubmenuClosed.value = true;
        emits('close-menu');
    }

    function onHandleClick() {
        if (!props.menu.href) {
            emits('click');
            return;
        }

        closeSubmenu();
    }
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    /* mobile-first */
    .navbar__menu-item-wrapper {
        height: 100%;

        button {
            width: 100%;
        }
    }

    .navbar__menu-item-icon-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
    }

    .navbar__menu-item {
        @include text.getFontBySize(primary-650);

        font-weight: var(--joy-font-weight-bold);
        text-align: inherit;
        display: flex;
        justify-content: space-between;
        color: var(--joy-color-neutral-70);
        text-decoration: none;
        cursor: pointer;
        user-select: none;
        gap: var(--joy-core-spacing-3);

        &:focus-visible {
            outline: var(--joy-core-spacing-0) solid var(--joy-color-neutral-40);
            outline-offset: calc(1 * var(--joy-core-spacing-0));
        }

        &--active {
            color: var(--joy-color-neutral-40);
        }

        span,
        &-icon-wrapper {
            display: block;
            align-self: center;
        }

        &-submenu-labels {
            @include text.getFontBySize(primary-300);

            font-weight: var(--joy-font-weight-normal);
            color: var(--joy-color-neutral-40);
            margin-top: var(--joy-core-spacing-1);
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
        }
    }

    /* desktop */
    @include mq.screen_md(min) {
        .navbar__menu-item-wrapper {
            .navbar__menu-item-label {
                &::before {
                    content: '';
                    position: absolute;
                    bottom: -6px;
                    left: 0;
                    right: 0;
                    height: 2px;
                    background-color: var(--navbar-menu-item-underline-color, var(--joy-color-primary-50));
                    transform: scaleX(0);
                    transition: transform var(--joy-transition-duration-default) var(--navbar-transition-easing);
                    transform-origin: left;
                }
            }

            &:hover {
                .navbar__submenu {
                    display: flex;
                }
            }

            &--close-submenu:hover .navbar__submenu {
                display: none;
            }
        }

        .navbar__menu-item {
            @include text.getFontBySize(primary-300);

            font-weight: var(--joy-font-weight-normal);
            display: flex;
            color: var(--navbar-text-color, var(--joy-color-neutral-60));
            text-decoration: none;
            transition: color var(--navbar-scroll-transition-duration) var(--navbar-transition-easing);
            padding: 0 var(--joy-core-spacing-4);
            white-space: nowrap;
            position: relative;
            user-select: none;
            height: 100%;
            align-items: flex-end;
            justify-content: center;
            cursor: pointer;

            &-label {
                position: relative;
            }

            &-wrapper:hover &-label,
            &--active &-label {
                &::before {
                    transform: scaleX(1);
                }
            }

            &-label-wrapper {
                display: flex;
                height: 100%;
                align-items: center;
            }

            &-icon-wrapper,
            &-submenu-labels {
                display: none;
            }

            &:focus-visible {
                outline: var(--joy-core-spacing-0) solid var(--joy-color-neutral-40);
                border-radius: var(--joy-core-radius-4);
                outline-offset: calc(-3 * var(--joy-core-spacing-0));
            }
        }
    }
</style>
